import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import '../../universal.css';
import './ResetPassword.css';
import UniversalToast from '../../components/UniversalToast';
import { Eye, EyeOff } from 'lucide-react';

const ResetPasswordPrivate: React.FC = () => {
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [tokenValid, setTokenValid] = useState(true);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [showSuccessToast, setShowSuccessToast] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token');

    useEffect(() => {
        if (!token) {
            setTokenValid(false);
            setError('Invalid or missing reset token');
        }
    }, [token]);

    const validatePassword = (pwd: string) => {
        if (pwd.length < 8) return 'Password must be at least 8 characters long';
        if (!/(?=.*[a-z])/.test(pwd)) return 'Password must contain at least one lowercase letter';
        if (!/(?=.*[A-Z])/.test(pwd)) return 'Password must contain at least one uppercase letter';
        if (!/(?=.*\d)/.test(pwd)) return 'Password must contain at least one number';
        return null;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (!password || !confirmPassword) {
            setError('Please fill in all fields');
            return;
        }

        const passwordError = validatePassword(password);
        if (passwordError) {
            setError(passwordError);
            return;
        }

        if (password !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        if (!token) {
            setError('Invalid reset token');
            return;
        }

        setLoading(true);
        try {
            const response = await api.post('/auth/reset-password', {
                token,
                newPassword: password
            });

            const { user, token: authToken } = response.data;

            localStorage.setItem('user', JSON.stringify(user));
            localStorage.setItem('token', authToken);

            api.defaults.headers.common['Authorization'] = `Bearer ${authToken}`;

            window.dispatchEvent(new CustomEvent('userLogin'));
            window.dispatchEvent(new CustomEvent('refreshFeed'));

            // Show success toast then navigate to Settings
            setSuccessMessage('Password updated successfully');
            setShowSuccessToast(true);
            setTimeout(() => {
                setShowSuccessToast(false);
                navigate('/settings');
            }, 2000);
        } catch (err: any) {
            if (err.response?.status === 400) {
                setError(err.response.data.error || 'Invalid or expired reset token');
                setTokenValid(false);
            } else {
                setError('Failed to reset password. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    const canSubmit = (() => {
        if (loading) return false;
        if (!password || !confirmPassword) return false;
        if (validatePassword(password)) return false;
        if (password !== confirmPassword) return false;
        if (!token) return false;
        return true;
    })();

    if (!tokenValid) {
        return (
            <div className="priv-reset-container">
                <div className="priv-auth-card">
                    <h1 className="priv-auth-title">Invalid Link</h1>
                    <p className="priv-auth-subtitle">This password reset link is invalid or has expired</p>
                    <div className="priv-error-message">{error || 'The reset link may have expired or been used already.'}</div>
                </div>
            </div>
        );
    }

    return (
        <div className="priv-reset-container">
            <div className="priv-auth-card">
                <h1 className="priv-auth-title">Reset Password</h1>
                <p className="priv-auth-subtitle">Enter your new password</p>

                <form onSubmit={handleSubmit} className="priv-auth-form">
                    {loading && (
                        <div className="priv-loading-message">Resetting password and logging you in...</div>
                    )}

                    <div className="priv-form-group">
                        <label htmlFor="password">New Password</label>
                        <div className="priv-password-input-container">
                            <input
                                type={showPassword ? 'text' : 'password'}
                                id="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Enter new password"
                                disabled={loading}
                                autoComplete="new-password"
                            />
                            <button
                                type="button"
                                className="priv-password-toggle"
                                onClick={() => setShowPassword(!showPassword)}
                                aria-label={showPassword ? 'Hide password' : 'Show password'}
                            >
                                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        </div>
                    </div>

                    <div className="priv-form-group">
                        <label htmlFor="confirmPassword">Confirm New Password</label>
                        <div className="priv-password-input-container">
                            <input
                                type={showConfirmPassword ? 'text' : 'password'}
                                id="confirmPassword"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                placeholder="Confirm new password"
                                disabled={loading}
                                autoComplete="new-password"
                            />
                            <button
                                type="button"
                                className="priv-password-toggle"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                            >
                                {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        </div>
                    </div>

                    <div className="priv-password-requirements">
                        <p>Password requirements:</p>
                        <ul>
                            <li>At least 8 characters long</li>
                            <li>One lowercase letter</li>
                            <li>One uppercase letter</li>
                            <li>One number</li>
                        </ul>
                    </div>

                    {error && <div className="priv-error-message">{error}</div>}

                    <button type="submit" className="priv-auth-button" disabled={!canSubmit}>
                        {loading ? 'Resetting...' : 'Reset Password'}
                    </button>
                </form>
            </div>

            {/* Toast */}
            <UniversalToast type="success" message={successMessage} show={showSuccessToast} />
        </div>
    );
};

export default ResetPasswordPrivate;


